import type { DevicePinLock } from './types';

const PIN_ITERATIONS = 210_000;

function bytesToBase64(bytes: Uint8Array): string {
  let value = '';
  for (const byte of bytes) value += String.fromCharCode(byte);
  return btoa(value);
}

function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(value), char => char.charCodeAt(0)) as Uint8Array<ArrayBuffer>;
}

async function derivePin(pin: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<Uint8Array<ArrayBuffer>> {
  const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, material, 256);
  return new Uint8Array(bits);
}

export async function createDevicePinLock(pin: string): Promise<DevicePinLock> {
  if (!/^\d{4,8}$/.test(pin)) throw new Error('Die Geräte-PIN muss aus 4 bis 8 Ziffern bestehen.');
  if (!crypto.subtle) throw new Error('Sichere PIN-Speicherung wird von diesem Browser nicht unterstützt.');
  const salt = crypto.getRandomValues(new Uint8Array(16)) as Uint8Array<ArrayBuffer>;
  const hash = await derivePin(pin, salt, PIN_ITERATIONS);
  return { salt: bytesToBase64(salt), hash: bytesToBase64(hash), iterations: PIN_ITERATIONS };
}

export async function verifyDevicePin(pin: string, lock: DevicePinLock): Promise<boolean> {
  if (!crypto.subtle || !/^\d{4,8}$/.test(pin)) return false;
  const computed = await derivePin(pin, base64ToBytes(lock.salt), lock.iterations);
  const expected = base64ToBytes(lock.hash);
  if (computed.length !== expected.length) return false;
  let mismatch = 0;
  for (let index = 0; index < computed.length; index += 1) mismatch |= computed[index] ^ expected[index];
  return mismatch === 0;
}
